import { gql, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconLock from '@apollo/icons/default/IconLock.svg';
import IconPublic from '@apollo/icons/default/IconPublic.svg';
import IconUnavailable from '@apollo/icons/default/IconUnavailable.svg';
import {
  Badge,
  Group,
  Link,
  Table,
  TableContainer,
  TableSortButton,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import { orderBy } from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { VariantEndpointURL } from 'src/app/account/supergraphListPage/subgraphEndpoints/VariantEndpointURL';
import { InitiatingCloudEndpointBadge } from 'src/app/appLayout/components/globalHeaderGraphContent/initiatingCloudEndpointBadge/InitiatingCloudEndpointBadge';
import { AddEndpointModal } from 'src/app/graph/quickDeploy/components/AddEndpointModal';
import { EndpointStatus } from 'src/app/graph/quickDeploy/components/EndpointStatus';
import { variantHomepageRouteConfig } from 'src/app/graph/routes';
import { ShimmerLoading } from 'src/components/common/loading/ShimmerLoading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { FavoriteButton } from 'src/components/favoriteButton/FavoriteButton';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useHandleFavoriteVariant } from 'src/hooks/useHandleFavoriteVariant';
import { IsCloudEndpointInitiatingContextProvider } from 'src/hooks/useIsCloudEndpointInitiating';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { useShowDeployUI } from 'src/lib/featureFlags';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const SupergraphListGraphDeferredMetadataFragment = gql`
  fragment SupergraphListGraphDeferredMetadataFragment on Service {
    id
    graphType
    variants {
      id
      requestsInLastDay
      usageLastReportedAt
      subgraphs {
        name
      }
      entities {
        ... on EntitiesResponse {
          entities {
            typename
          }
        }
      }
    }
  }
`;

const calcRequestsPerMin = (requestsInLastDay: number) => {
  const totalMinutes = moment.duration(24, 'hours').asMinutes();
  return format.requests(requestsInLastDay / totalMinutes);
};

const MaybeUnavailable = ({
  value,
  unavailableTitle,
}: {
  value: string | null;
  unavailableTitle?: string;
}) => {
  const unavailable = !value || value === '-';
  return (
    <Tooltip label={unavailable ? unavailableTitle : undefined}>
      {unavailable ? (
        <IconUnavailable className="text-disabled" />
      ) : (
        <span>{value}</span>
      )}
    </Tooltip>
  );
};

enum SortableField {
  name = 'name',
  subgraphs = 'subgraphs',
  lastPublished = 'activeSchemaPublish.publishedAt',
  types = 'activeSchemaPublish.schema.typeCount',
  fields = 'activeSchemaPublish.schema.fieldCount',
  requestsPerMin = 'requestsInLastDay',
  setupProgress = 'setupProgress',
  entities = 'entities',
}

const SupergraphsVariantsTableQuery = gql`
  query SupergraphsVariantsTableQuery($graphId: ID!) {
    graph: service(id: $graphId) {
      id
      ...SupergraphListGraphDeferredMetadataFragment
    }
  }
  ${SupergraphListGraphDeferredMetadataFragment}
`;

export const SupergraphsVariantsTable = ({
  isInFavSection,
  inView,
  graph: { id: graphId, variants, graphType, roles, title: graphTitle },
}: {
  isInFavSection: boolean;
  inView: boolean;
  graph: GraphQLTypes.SupergraphsListItemGraphFragment;
}) => {
  const location = useLocation();
  const { handleFavorite } = useHandleFavoriteVariant();
  const showEndpointColumns =
    useShowDeployUI() &&
    (graphType === GraphQLTypes.GraphType.CLASSIC ||
      graphType === GraphQLTypes.GraphType.SELF_HOSTED_SUPERGRAPH);

  // Modal state for adding endpoint
  const [addEndpointModal, setAddEndpointModal] = useState<{
    isOpen: boolean;
    variantName: string;
  }>({ isOpen: false, variantName: '' });
  const { data, loading, error } = useQuery<
    GraphQLTypes.SupergraphsVariantsTableQuery,
    GraphQLTypes.SupergraphsVariantsTableQueryVariables
  >(SupergraphsVariantsTableQuery, {
    variables: { graphId },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    skip: !inView,
  });

  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [selectedField, setSelectedField] = useState<SortableField>(
    SortableField.name,
  );

  const handleSort = ({
    field,
    nextAscending,
  }: {
    field: SortableField;
    nextAscending: boolean;
  }) => {
    setSelectedField(field);
    setSortAscending(nextAscending);
  };

  const { initiatingSupergraphState } = useRouteParams(orgRoutes.graphs);

  const sortedVariants = useMemo(() => {
    const variantsMergedWithStats = variants.map((variant) => {
      const variantStats = data?.graph?.variants.find(
        (v) => v.id === variant.id,
      );
      return {
        ...variant,
        subgraphs: variantStats?.subgraphs,
        requestsInLastDay: variantStats?.usageLastReportedAt
          ? variantStats.requestsInLastDay || 0
          : undefined,
        entities:
          variantStats?.entities?.__typename === 'EntitiesResponse'
            ? variantStats.entities.entities.length
            : undefined,
      };
    });
    return orderBy(
      isInFavSection
        ? variantsMergedWithStats?.filter(
            ({ isFavoriteOfCurrentUser }) => isFavoriteOfCurrentUser,
          )
        : variantsMergedWithStats,
      selectedField,
      sortAscending ? 'asc' : 'desc',
    );
  }, [selectedField, isInFavSection, sortAscending, data, variants]);

  if (error) {
    return (
      <ErrorMessage>
        Hmm, something went wrong when trying to load this graph. Please try
        reloading your browser.
      </ErrorMessage>
    );
  }

  return (
    <TableContainer className="overflow-x-auto">
      <Table
        size="condensed"
        className="w-full table-auto"
        sortBy={selectedField}
        sortOrder={sortAscending ? 'asc' : 'desc'}
        onRequestSort={({ sortBy, sortOrder }) => {
          handleSort({ field: sortBy, nextAscending: sortOrder === 'asc' });
        }}
      >
        <Thead>
          <Tr>
            <Th className="w-full">
              <Group>
                <span className="text-title">Variant name</span>
                <TableSortButton attribute={SortableField.name} />
              </Group>
            </Th>
            <Th className="w-1 whitespace-nowrap">
              <Group>
                <span className="text-title">Last published</span>
                <TableSortButton attribute={SortableField.lastPublished} />
              </Group>
            </Th>
            <Th isNumeric className="w-1 whitespace-nowrap">
              <Group className="justify-end">
                <TableSortButton attribute={SortableField.types} />
                <span className="text-title">Types</span>
              </Group>
            </Th>
            <Th isNumeric className="w-1 whitespace-nowrap">
              <Group className="justify-end">
                <TableSortButton attribute={SortableField.fields} />
                <span className="text-title">Fields</span>
              </Group>
            </Th>
            {roles?.canQueryStats && (
              <>
                {roles?.canQueryImplementingServices && (
                  <Th isNumeric className="w-1 whitespace-nowrap">
                    <Group className="justify-end">
                      <TableSortButton attribute={SortableField.subgraphs} />
                      <span className="text-title">Subgraphs</span>
                    </Group>
                  </Th>
                )}
                <Th isNumeric className="w-1 whitespace-nowrap">
                  <Group className="justify-end">
                    <TableSortButton attribute={SortableField.entities} />
                    <span className="text-title">Entities</span>
                  </Group>
                </Th>
                <Th isNumeric className="w-1 whitespace-nowrap">
                  <Group className="justify-end">
                    <TableSortButton attribute={SortableField.requestsPerMin} />
                    <span className="text-title">Requests/min</span>
                  </Group>
                </Th>
              </>
            )}
            {showEndpointColumns && (
              <>
                <Th className="w-44 max-w-44 whitespace-nowrap">
                  <Group gap={2}>
                    <span className="text-title">Endpoint URL</span>{' '}
                    <Tooltip
                      label="The GraphQL endpoint URL for this variant"
                      interactive
                    >
                      <IconInfo className="text-icon-primary" />
                    </Tooltip>
                  </Group>
                </Th>
                <Th className="w-1 whitespace-nowrap">
                  <Group>
                    <span className="text-title">Endpoint status</span>
                  </Group>
                </Th>
              </>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {sortedVariants.length === 0 ? (
            <Tr key="current">
              <Td colSpan={2}>
                <Group gap={2}>
                  <Group gap={2} className="flex-wrap">
                    <Link
                      data-testid={`supergraph-list-variant-link-${graphId}-${Config.service.defaultVariant}`}
                      to={variantHomepageRouteConfig.location({
                        graphId,
                        graphVariant: Config.service.defaultVariant,
                      })}
                      className="break-all"
                    >
                      {Config.service.defaultVariant}
                    </Link>
                  </Group>
                </Group>
              </Td>
              <Td>
                <div className="whitespace-nowrap text-base">Never</div>
              </Td>
              <Td isNumeric>
                <MaybeUnavailable
                  unavailableTitle="Data will appear after your first publish."
                  value={null}
                />
              </Td>
              <Td isNumeric>
                <MaybeUnavailable
                  unavailableTitle="Data will appear after your first publish."
                  value={null}
                />
              </Td>
              <Td isNumeric>
                <MaybeUnavailable
                  unavailableTitle="Data will appear after your first publish."
                  value={null}
                />
              </Td>
              <Td isNumeric>
                <MaybeUnavailable
                  unavailableTitle="Data will appear after your first publish."
                  value={null}
                />
              </Td>
              <Td isNumeric>
                <MaybeUnavailable
                  unavailableTitle="Data will appear after your first publish."
                  value={null}
                />
              </Td>
            </Tr>
          ) : (
            sortedVariants.map((variant) => {
              const {
                id,
                name,
                url,
                activeSchemaPublish,
                entities,
                isProtected,
                isContract,
                isPublic,
                isFavoriteOfCurrentUser,
                latestLaunch,
                hasManagedSubgraphs,
                requestsInLastDay,
                subgraphs,
              } = variant;

              const buildErrors =
                latestLaunch?.build?.result?.__typename === 'BuildFailure'
                  ? latestLaunch.build.result.errorMessages
                  : [];

              return (
                <Tr key={id}>
                  <Td className="w-full">
                    <Group gap={2} className="min-w-0">
                      <FavoriteButton
                        className="flex-shrink-0 !px-0"
                        data-testid="supergraphs-list-favorite-button"
                        isFavorite={isFavoriteOfCurrentUser}
                        ariaLabel={`Click to ${
                          isFavoriteOfCurrentUser ? 'unfavorite' : 'favorite'
                        } ${name} variant`}
                        onClick={() => {
                          handleFavorite({
                            serviceId: graphId,
                            favorite: !isFavoriteOfCurrentUser,
                            variantName: name,
                            variantId: id,
                          });
                        }}
                      />
                      <Group gap={2} className="min-w-0 flex-wrap">
                        <Link
                          {...(initiatingSupergraphState &&
                          initiatingSupergraphState.graphId === graphId &&
                          initiatingSupergraphState.graphVariant === name
                            ? {
                                'data-analytics-category': '6M2S: Onboarding',
                                'data-analytics-label':
                                  'Variant link click immediately after creation',
                              }
                            : {})}
                          data-testid={`supergraph-list-variant-link-${graphId}-${name}`}
                          to={variantHomepageRouteConfig.location({
                            graphId,
                            graphVariant: name,
                          })}
                          className="break-words text-sm font-semibold underline"
                        >
                          {name}
                        </Link>
                        {initiatingSupergraphState && (
                          <IsCloudEndpointInitiatingContextProvider
                            graphRef={{ graphId, graphVariant: name }}
                          >
                            <InitiatingCloudEndpointBadge />
                          </IsCloudEndpointInitiatingContextProvider>
                        )}
                        {isProtected && (
                          <Tooltip label="Protected variants can only be modified by Graph Admins and Org Admins">
                            <IconLock className="icon-warning flex-shrink-0" />
                          </Tooltip>
                        )}
                        {isPublic && (
                          <Tooltip label="Public">
                            <IconPublic className="icon-info flex-shrink-0" />
                          </Tooltip>
                        )}
                        {isContract && <Badge>Contract</Badge>}
                        {graphType !==
                          GraphQLTypes.GraphType.CLOUD_SUPERGRAPH &&
                          hasManagedSubgraphs && <Badge>Federated</Badge>}
                        {isMinLengthArray(1, buildErrors) && (
                          <ReactRouterLink
                            className="inline-flex"
                            to={setOverlayVisibleLocation({
                              location,
                              additionalSearchParameters: {
                                graphId,
                                variant: name,
                              },
                              overlayName: Config.modals.buildErrors,
                            })}
                          >
                            <Badge variant="error">Build Failed</Badge>
                          </ReactRouterLink>
                        )}
                      </Group>
                    </Group>
                  </Td>
                  <Td>
                    <div className="whitespace-nowrap text-sm">
                      {activeSchemaPublish ? (
                        <RelativeTimestamp
                          value={activeSchemaPublish.publishedAt}
                          withTooltip={false}
                        />
                      ) : (
                        'never'
                      )}
                    </div>
                  </Td>
                  <Td isNumeric>
                    <div className="text-right font-mono text-sm">
                      <MaybeUnavailable
                        unavailableTitle="No schema has been published"
                        value={
                          activeSchemaPublish
                            ? format.number(
                                activeSchemaPublish.schema.typeCount,
                                '0.[.]0a',
                              )
                            : null
                        }
                      />
                    </div>
                  </Td>
                  <Td isNumeric>
                    <div className="text-right font-mono text-sm">
                      <MaybeUnavailable
                        unavailableTitle="No schema has been published"
                        value={
                          activeSchemaPublish
                            ? format.number(
                                activeSchemaPublish.schema.fieldCount,
                                '0.[.]0a',
                              )
                            : null
                        }
                      />
                    </div>
                  </Td>

                  {roles?.canQueryStats && (
                    <>
                      {roles?.canQueryImplementingServices && (
                        <Td isNumeric>
                          <div className="text-right font-mono text-sm">
                            {loading ? (
                              <ShimmerLoading className="my-3.5 w-full" />
                            ) : roles?.canQueryImplementingServices &&
                              subgraphs ? (
                              <MaybeUnavailable
                                unavailableTitle="This variant has no subgraphs"
                                value={subgraphs.length.toLocaleString()}
                              />
                            ) : null}
                          </div>
                        </Td>
                      )}
                      <Td isNumeric>
                        <div className="text-right font-mono text-sm">
                          {loading ? (
                            <ShimmerLoading className="my-3.5 w-full" />
                          ) : entities === undefined ? null : (
                            format.number(entities, '0.[.]0a')
                          )}
                        </div>
                      </Td>
                      <Td isNumeric>
                        <div className="text-sm">
                          {loading ? (
                            <ShimmerLoading className="my-3.5 w-full" />
                          ) : (
                            <MaybeUnavailable
                              unavailableTitle="No metrics have been sent"
                              value={
                                requestsInLastDay
                                  ? calcRequestsPerMin(requestsInLastDay)
                                  : null
                              }
                            />
                          )}
                        </div>
                      </Td>
                    </>
                  )}
                  {showEndpointColumns && (
                    <>
                      <Td className="w-44 max-w-44 whitespace-nowrap">
                        <VariantEndpointURL
                          url={url}
                          graphId={graphId}
                          variantName={name}
                          onSetEndpoint={() => {
                            setAddEndpointModal({
                              isOpen: true,
                              variantName: name,
                            });
                          }}
                        />
                      </Td>
                      <Td>
                        <EndpointStatus url={url} inView={inView} />
                      </Td>
                    </>
                  )}
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>

      <AddEndpointModal
        isOpen={addEndpointModal.isOpen}
        onClose={() => {
          setAddEndpointModal({ isOpen: false, variantName: '' });
        }}
        graphId={graphId}
        graphName={graphTitle}
        variantName={addEndpointModal.variantName}
        graphType={graphType}
        hasManagedSubgraphs={
          variants.find((v) => v.name === addEndpointModal.variantName)
            ?.hasManagedSubgraphs
        }
      />
    </TableContainer>
  );
};
